import { useFileScope } from '../../../../hooks/signalr/useFileScope'
import { useAuth } from '../../../../hooks'
import React, { Fragment, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
	AutoAwesome,
	Close,
	InfoOutlined,
	InsertDriveFile,
} from '@mui/icons-material'
import { ClassNameValue } from 'tailwind-merge'
import {
	CurrentDocumentData,
	DocumentData,
} from '../../../../lib/api/types/document.interfaces'
import { useFile, useThumbnail } from '../../../../hooks/api/documentHooks'
import FileFormatIcon from '../../../common/FileFormatIcon'
import { IconButton, Tooltip } from '../../../ui'
import cn from '../../../../lib/utils/cn'
import {
	getFileNameAndExt,
	RENDERING_TYPES,
	shortenFileName,
} from '../../../../lib/utils/formatDocument'
import Loader from '../../../common/Loader'
import InfoTab from './components/InfoTab'
import AiFormTab from './components/AiFormTab'
import { Features, useBilling } from '../../../../context/billingContext'
import { useMediaQuery } from 'react-responsive'

interface FileInfoModalProps {
	manageFileScope?: boolean
	isOpen: boolean
	onClose: () => void
	fileInfo: DocumentData | CurrentDocumentData | null
	classNameHeader?: ClassNameValue
	classNameContentWrapper?: ClassNameValue
}

const FileInfoModal: React.FC<FileInfoModalProps> = ({
	isOpen,
	onClose,
	fileInfo: initialFileInfo,
	manageFileScope = true,
	classNameHeader,
	classNameContentWrapper,
}) => {
	const { isAuthenticated } = useAuth()
	const isFile = Boolean(initialFileInfo &&
		(!('type' in initialFileInfo) || initialFileInfo.type === 0))
	const fileId = isFile ? initialFileInfo?.id ?? null : null
	useFileScope(fileId, isOpen && manageFileScope && isAuthenticated)
	const { data: currentFile } = useFile({ id: fileId ?? '' }, {
		enabled: isOpen && Boolean(fileId) && isAuthenticated,
	})
	const fileInfo = currentFile?.data ?? initialFileInfo
	const { hasFeature } = useBilling()
	const isMobile = useMediaQuery({ maxWidth: 480 })
	const [isInfoTab, setIsInfoTab] = useState(true)
	const [isEditMode, setIsEditMode] = useState(false)

	const fileType = getFileNameAndExt(fileInfo?.name)?.ext
	const isAiTabDisabled =
		!hasFeature(Features.Classification) || !fileInfo?.size
	const aiTabTooltipLabel = !fileInfo?.size
		? 'Not available for empty files'
		: !hasFeature(Features.Classification)
			? 'Upgrade to use AI features'
			: null

	const {
		data: thumbnailData,
		isLoading: isLoadingThumbnail,
		isRefetching: isRefetchingThumbnail,
	} = useThumbnail(
		{
			id: fileInfo?.id || '',
			// versionId,
		},
		{
			enabled:
				isOpen &&
				Boolean(
					fileInfo?.id &&
					fileInfo.size &&
					isAuthenticated &&
					fileType &&
					RENDERING_TYPES?.includes(fileType)
				),
		}
	)

	const handleClose = () => {
		setIsInfoTab(true)
		setIsEditMode(false)
		onClose()
	}

	if (!isOpen || !fileInfo) return null

	return (
		<Fragment>
			<AnimatePresence>
				{isOpen && (
					<motion.div
						key="backdrop"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						className={cn(
							'fixed inset-0 bg-black/20 z-50',
							isMobile ? 'flex' : 'flex justify-end'
						)}
						onClick={() => {
							!isEditMode && handleClose()
						}}
					>
						<motion.div
							key="modal"
							initial={{ x: isMobile ? 0 : '100%' }}
							animate={{ x: 0 }}
							exit={{ x: isMobile ? 0 : '100%' }}
							transition={{ duration: isMobile ? 0 : 0.25, ease: 'easeInOut' }}
							className={cn(
								'bg-white dark:bg-background-dark h-full shadow-lg relative',
								isMobile ? 'w-screen' : 'w-[320px] md:w-[480px]'
							)}
							onClick={(e) => e.stopPropagation()}
						>
							<div
								className={cn(
									'p-6 border-b border-stroke-light dark:border-stroke-light-dark  bg-white dark:bg-background-dark flex justify-between items-center box-border min-h-20 h-20 absolute top-0 inset-x-0',
									classNameHeader
								)}
							>
								<h2 className="text-xl font-bold flex items-center gap-2 dark:text-text-main-dark">
									<InfoOutlined /> File Info
								</h2>
								<IconButton
									variant="transparent"
									onClick={handleClose}
									icon={<Close className="dark:text-text-main-dark" />}
								/>
							</div>
							<div
								className={cn(
									'pt-20 flex flex-col overflow-hidden',
									classNameContentWrapper
								)}
								style={{ height: 'calc(100vh - 80px)' }}
							>
								<div className="flex-1 overflow-y-auto">
									<div className="py-7 px-4 ">
										<div className="text-lg font-medium flex items-center mb-4 dark:text-text-main-dark dark:fill-text-main-dark">
											<FileFormatIcon
												fileName={fileInfo?.name}
												className="mr-2"
												size={20}
											/>
											{shortenFileName(fileInfo?.name, 22)}
										</div>

										{thumbnailData?.data?.downloadUrl ? (
											<div className="w-full h-fit min-h-48 flex items-center justify-center">
												<img
													src={thumbnailData.data.downloadUrl}
													alt="Thumbnail"
													className="w-fit max-h-60 object-contain rounded shadow-md"
												/>
											</div>
										) : (
											<div className="w-full h-48 flex items-center justify-center rounded bg-background-light dark:bg-background-light-dark">
												{isLoadingThumbnail || isRefetchingThumbnail ? (
													<Loader size={30} />
												) : (
													!thumbnailData?.data?.downloadUrl && (
														<InsertDriveFile className="text-text-secondary" />
													)
												)}
											</div>
										)}
									</div>

									<div className="relative flex h-12 bg-white dark:bg-background-dark border-b border-stroke-light dark:border-stroke-light-dark ">
										<button
											onClick={() => setIsInfoTab(true)}
											className={cn(
												'flex items-center justify-center w-1/2 p-3 duration-default font-semibold text-text-secondary bg-white hover:text-text-main hover:fill-text-main text-base',
												'dark:bg-background-dark dark:hover:text-text-main-dark dark:hover:fill-text-main-dark ',
												isInfoTab &&
												'text-text-main dark:text-text-main-dark fill-text-main'
											)}
										>
											File Info
										</button>
										<Tooltip
											label={aiTabTooltipLabel}
											classNameChildrenWrapper="flex justify-center w-1/2"
										>
											<button
												disabled={isAiTabDisabled}
												onClick={() => {
													if (!isAiTabDisabled) setIsInfoTab(false)
												}}
												className={cn(
													'flex justify-center gap-2 items-center w-full p-3 duration-default font-semibold text-text-secondary text-base bg-white fill-text-secondary hover:text-text-main hover:fill-text-main',
													'dark:text-text-table-dark dark:bg-background-dark dark:fill-text-table-dark',
													!isInfoTab &&
													'text-text-main dark:text-text-main-dark fill-text-main dark:fill-text-main-dark',
													isAiTabDisabled &&
													'pointer-events-none opacity-50'
												)}
											>
												<AutoAwesome className="w-5 h-5 fill-inherit" />
												<span>AI</span>
											</button>
										</Tooltip>
										<div
											className={cn(
												'absolute w-32 md:w-40 bottom-0 h-1 bg-accent rounded-t-full',
												isInfoTab ? 'left-4 md:left-10' : 'right-4 md:right-10'
											)}
										/>
									</div>

									<div className="py-7 px-5 md:px-7">
										{isInfoTab ? (
											<InfoTab fileInfo={fileInfo} />
										) : (
											<AiFormTab
												fileInfo={fileInfo}
												isEditMode={isEditMode}
												onToggleEditMode={setIsEditMode}
											/>
										)}
									</div>
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</Fragment>
	)
}

export default FileInfoModal
